import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Avatar,
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import DashboardCustomizeRoundedIcon from '@mui/icons-material/DashboardCustomizeRounded';
import { supabase } from '../../../config/supabaseClient';
import queryClient from '../../../config/queryClient';

const inputSx = {
  mb: 2,
  '& .MuiOutlinedInput-root': {
    color: '#f8fafc',
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    '& fieldset': { borderColor: '#334155' },
    '&:hover fieldset': { borderColor: '#94a3b8' },
    '&.Mui-focused fieldset': { borderColor: '#38bdf8' },
  },
  '& .MuiInputLabel-root': { color: '#94a3b8' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#38bdf8' },
};

type ProjectVitalLocationState = {
  from?: {
    pathname?: string;
  };
};

const ProjectVitalLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as ProjectVitalLocationState | null;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const redirectTo = state?.from?.pathname || '/project-vital';

  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // IND-058: se vacía antes de entrar: después cancelaría lo que la sesión
      // nueva ya está pidiendo (p. ej. el rol industrial del operario).
      queryClient.clear();
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        throw signInError;
      }

      navigate(redirectTo, { replace: true });
    } catch (err: any) {
      if (
        err.message &&
        err.message.toLowerCase().includes('invalid login credentials')
      ) {
        setError('Usuario o contraseña incorrectos.');
      } else {
        setError(err.message || 'No se pudo iniciar sesión.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#020617',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 4,
      }}
    >
      <Container maxWidth="xs">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            bgcolor: '#0f172a',
            border: '1px solid rgba(56, 189, 248, 0.16)',
            borderRadius: 4,
            boxShadow: '0 0 40px rgba(0, 0, 0, 0.35)',
          }}
        >
          <Avatar
            sx={{
              m: 1,
              bgcolor: alpha('#38bdf8', 0.12),
              color: '#38bdf8',
              border: '1px solid rgba(56, 189, 248, 0.28)',
            }}
          >
            <DashboardCustomizeRoundedIcon />
          </Avatar>

          <Typography
            component="h1"
            variant="h5"
            sx={{
              fontWeight: 800,
              color: '#f8fafc',
              mt: 1,
              mb: 1,
            }}
          >
            Proyecto Vital
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: '#94a3b8',
              mb: 3,
              textAlign: 'center',
            }}
          >
            Inicia sesión para acceder a tu espacio personal.
          </Typography>

          {error && (
            <Alert
              severity="error"
              variant="outlined"
              sx={{
                width: '100%',
                mb: 3,
                borderColor: '#f87171',
                color: '#f87171',
                '& .MuiAlert-icon': {
                  color: '#f87171',
                },
              }}
            >
              <AlertTitle>Error de acceso</AlertTitle>
              {error}
            </Alert>
          )}

          <Box
            component="form"
            onSubmit={handleLogin}
            noValidate
            sx={{
              mt: 1,
              width: '100%',
            }}
          >
            <TextField
              margin="normal"
              required
              fullWidth
              id="project-vital-email"
              label="Correo electrónico"
              name="email"
              type="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              disabled={loading}
              sx={inputSx}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              id="project-vital-password"
              label="Contraseña"
              name="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={loading}
              sx={inputSx}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                mt: 3,
                py: 1.5,
                bgcolor: '#38bdf8',
                color: '#0f172a',
                fontWeight: 800,
                '&:hover': {
                  bgcolor: '#0ea5e9',
                },
                '&.Mui-disabled': {
                  bgcolor: '#334155',
                  color: '#94a3b8',
                },
              }}
            >
              {loading ? 'Accediendo...' : 'Entrar'}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ProjectVitalLoginPage;
